import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

interface PreviewProps {
  /** Full iframe src URL to preview (example: https://www.youtube.com/embed/dQw4w9WgXcQ) */
  src?: string;
  /** Optional user ID for constructing sandbox URL */
  userId?: string | null;
  /** Optional project ID for constructing sandbox URL */
  projectId?: number | string | null;
  /** Optional title for accessibility */
  title?: string;
  /** Optional className to style the wrapper */
  className?: string;
  /** If true, allow fullscreen */
  allowFullScreen?: boolean;
  /** Additional iframe attributes (e.g., allow string) */
  allow?: string;
  /** Optional callback when an element is selected. Receives an object with selection details. */
  onElementSelected?: (info: Record<string, any>) => void;
}

/**
 * Preview component with a floating element-selector overlay.
 * - If the iframe is same-origin, we inject a highlight overlay into the iframe and read element info.
 * - If cross-origin, we fall back to capturing coordinates and returning limited info.
 * Usage: <Preview src="https://example.com" onElementSelected={(info) => console.log(info)} />
 */
const Preview: React.FC<PreviewProps> = ({
  src,
  userId,
  projectId,
  title = 'Preview content',
  className = '',
  allowFullScreen = true,
  allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  onElementSelected
}) => {
  // Construct the preview URL
  const previewSrc = useMemo(() => {
    // If both userId and projectId are provided, construct sandbox URL
    if (userId && projectId) {
      // Use first 5 characters of userId and projectId
      const userIdStr = String(userId).trim();
      const projectIdStr = String(projectId).trim();
      
      // Get first 5 characters, or pad with zeros if shorter
      const first5UserId = userIdStr.length >= 5 
        ? userIdStr.slice(0, 5) 
        : userIdStr.padStart(5, '0');
      const first5ProjectId = projectIdStr.length >= 5 
        ? projectIdStr.slice(0, 5) 
        : projectIdStr.padStart(5, '0');
      
      const url = `https://${first5UserId}--${first5ProjectId}.sandbox.vishmaker.com`;
      
      // Debug logging (can be removed in production)
      console.log('✅ Preview URL constructed:', {
        userId,
        projectId,
        userIdStr,
        projectIdStr,
        first5UserId,
        first5ProjectId,
        url
      });
      
      return url;
    }
    
    // Log warning if userId or projectId is missing
    console.warn('⚠️ Preview component missing required props:', {
      userId,
      projectId,
      src
    });
    
    // Otherwise use provided src or fallback
    return src || 'https://www.dev.vishmaker.com';
  }, [src, userId, projectId]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState<Record<string, any> | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [details, setDetails] = useState<Record<string, any> | null>(null);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);

  // Keep references to injected elements / handlers for cleanup
  const injected = useRef<any>({});

  const clearInjected = useCallback(() => {
    try {
      if (injected.current && injected.current.doc) {
        const { doc, hl, label, handlers } = injected.current;
        if (handlers) {
          doc.removeEventListener('mousemove', handlers.mousemove, true);
          doc.removeEventListener('click', handlers.click, true);
          doc.removeEventListener('keydown', handlers.keydown, true);
        }
        if (hl && hl.parentNode) hl.parentNode.removeChild(hl);
        if (label && label.parentNode) label.parentNode.removeChild(label);
      }
    } catch (e) {
      // ignore cross-origin or removal errors
    }
    // remove any parent overlay fallback
    if (injected.current.parentOverlay && injected.current.parentOverlay.parentNode) {
      injected.current.parentOverlay.parentNode.removeChild(injected.current.parentOverlay);
    }
    // remove any message listener / timeout used for postMessage handshake
    try {
      if (injected.current.messageHandler) {
        window.removeEventListener('message', injected.current.messageHandler);
      }
      if (injected.current.timeoutId) {
        clearTimeout(injected.current.timeoutId);
      }
    } catch (e) {
      // ignore
    }
    injected.current = {};
  }, []);

  useEffect(() => {
    // cleanup on unmount
    return () => {
      clearInjected();
    };
  }, [clearInjected]);

  const computeCssPath = (el: Element) => {
    if (!el || el.nodeType !== 1) return '';
    const parts: string[] = [];
    let node: Element | null = el;
    while (node && node.nodeType === 1) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        part += `#${node.id}`;
        parts.unshift(part);
        break; // id is unique enough
      } else {
        // add classes if present
        if (node.className && typeof node.className === 'string') {
          const cls = node.className.trim().split(/\s+/).join('.');
          if (cls) part += `.${cls}`;
        }
        const parent = node.parentElement;
        if (parent) {
          const siblings = Array.from(parent.children).filter(c => c.tagName === node!.tagName);
          if (siblings.length > 1) {
            const index = Array.from(parent.children).indexOf(node) + 1;
            part += `:nth-child(${index})`;
          }
        }
      }
      parts.unshift(part);
      node = node.parentElement;
    }
    return parts.join(' > ');
  };

  const enableSelector = useCallback(() => {
    setSelectMode(true);
    setSelectedInfo(null);
    clearInjected();

    // Try to access iframe document (same-origin).
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const win = iframe.contentWindow as Window | null;
      const doc = win?.document as Document | undefined;
      if (!doc) throw new Error('No iframe document');

      // create highlight box and label inside iframe
      const hl = doc.createElement('div');
      Object.assign(hl.style, {
        position: 'absolute',
        background: 'rgba(59,130,246,0.15)',
        border: '2px solid rgba(59,130,246,0.9)',
        pointerEvents: 'none',
        zIndex: '2147483647'
      } as CSSStyleDeclaration);
      const label = doc.createElement('div');
      Object.assign(label.style, {
        position: 'absolute',
        background: 'rgba(17,24,39,0.9)',
        color: 'white',
        padding: '2px 6px',
        fontSize: '12px',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: '2147483647'
      } as CSSStyleDeclaration);
      doc.body.appendChild(hl);
      doc.body.appendChild(label);

      const handlers: any = {};

      handlers.mousemove = (ev: MouseEvent) => {
        const target = ev.target as Element | null;
        if (!target || target === hl || target === label) return;
        const rect = (target as Element).getBoundingClientRect();
        Object.assign(hl.style, {
          left: `${rect.left + (win?.scrollX || 0)}px`,
          top: `${rect.top + (win?.scrollY || 0)}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`
        });
        label.textContent = `${(target as Element).tagName.toLowerCase()}${(target as Element).id ? `#${(target as Element).id}` : ''}`;
        label.style.left = `${rect.left + (win?.scrollX || 0)}px`;
        label.style.top = `${Math.max(0, rect.top - 22 + (win?.scrollY || 0))}px`;
      };

      handlers.click = (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = ev.target as Element | null;
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const info = {
          sameOrigin: true,
          tagName: target.tagName.toLowerCase(),
          id: (target as HTMLElement).id || undefined,
          className: (target as HTMLElement).className || undefined,
          boundingClientRect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          },
          cssPath: computeCssPath(target)
        };
        setSelectedInfo(info);
        if (onElementSelected) onElementSelected(info);
        // finish selection
        clearInjected();
        setSelectMode(false);
      };

      handlers.keydown = (ev: any) => {
        if (ev.key === 'Escape') {
          clearInjected();
          setSelectMode(false);
        }
      };

      doc.addEventListener('mousemove', handlers.mousemove, true);
      doc.addEventListener('click', handlers.click, true);
      doc.addEventListener('keydown', handlers.keydown, true);

      injected.current = { doc, hl, label, handlers };

      // Focus iframe so keydown works there
      try { (win as Window)?.focus(); } catch (e) {}
    } catch (err) {
      // Cross-origin iframe or access denied. First attempt a postMessage handshake with the iframe
      // so cooperating embedded pages can perform in-page selection and return element data.
      const container = containerRef.current;
      const iframe = iframeRef.current;
      if (!container || !iframe) return;

      const iframeOrigin = (() => {
        try {
          return new URL(previewSrc).origin;
        } catch (e) {
          return '*';
        }
      })();

      const nonce = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      let ackReceived = false;

      const fallbackToOverlay = () => {
        // create overlay fallback (coordinate capture)
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.02)',
          zIndex: '2147483647',
          cursor: 'crosshair'
        } as CSSStyleDeclaration);
        const hint = document.createElement('div');
        Object.assign(hint.style, {
          position: 'absolute',
          right: '8px',
          top: '8px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '6px 8px',
          borderRadius: '6px',
          fontSize: '12px'
        } as CSSStyleDeclaration);
        hint.textContent = 'Cross-origin iframe — selecting coordinates only. Click to capture.';
        overlay.appendChild(hint);

        const onMove = (ev: MouseEvent) => {
          const rect = container.getBoundingClientRect();
          const x = ev.clientX - rect.left;
          const y = ev.clientY - rect.top;
          // show a small marker
          let marker = overlay.querySelector('.__preview_marker__') as HTMLDivElement | null;
          if (!marker) {
            marker = document.createElement('div');
            marker.className = '__preview_marker__';
            Object.assign(marker.style, {
              position: 'absolute',
              width: '12px',
              height: '12px',
              background: 'rgba(59,130,246,0.9)',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none'
            } as CSSStyleDeclaration);
            overlay.appendChild(marker);
          }
          marker.style.left = `${x}px`;
          marker.style.top = `${y}px`;
        };

        const onClick = (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();
          const rect = container.getBoundingClientRect();
          const info = {
            sameOrigin: false,
            x: ev.clientX - rect.left,
            y: ev.clientY - rect.top,
            message: 'Cross-origin iframe: element details not accessible. Coordinates captured.'
          };
          setSelectedInfo(info);
          if (onElementSelected) onElementSelected(info);
          if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
          setSelectMode(false);
        };

        overlay.addEventListener('mousemove', onMove);
        overlay.addEventListener('click', onClick);
        container.appendChild(overlay);
        injected.current = { parentOverlay: overlay, handlers: { onMove, onClick } };
      };

      const messageHandler = (ev: MessageEvent) => {
        // Strict origin validation recommended in production
        if (iframeOrigin !== '*' && ev.origin !== iframeOrigin) return;
        const data = ev.data || {};
        if (!data || typeof data !== 'object') return;
        const { type, version, nonce: respNonce, payload } = data as any;
        if (version !== '1.0' || respNonce !== nonce) return;
        if (type === 'preview:handshake-ack') {
          ackReceived = true;
          // ask iframe to start select mode
          try {
            iframe.contentWindow?.postMessage({ type: 'preview:start-select', version: '1.0', nonce }, iframeOrigin);
          } catch (e) {
            // fall back if posting fails
            fallbackToOverlay();
          }
        } else if (type === 'preview:element-selected') {
          // payload from child contains element info
          setSelectedInfo({ sameOrigin: true, ...payload });
          if (onElementSelected) onElementSelected(payload);
          clearInjected();
          setSelectMode(false);
        } else if (type === 'preview:cancel') {
          clearInjected();
          setSelectMode(false);
        }
      };

      window.addEventListener('message', messageHandler);
      injected.current.messageHandler = messageHandler;

      // send handshake
      try {
        iframe.contentWindow?.postMessage({ type: 'preview:handshake', version: '1.0', nonce }, iframeOrigin);
      } catch (e) {
        // if posting failed immediately, fallback
        fallbackToOverlay();
        return;
      }

      // timeout: if no ack within 1.5s, fallback to overlay
      const timeoutId = setTimeout(() => {
        if (!ackReceived) {
          window.removeEventListener('message', messageHandler);
          fallbackToOverlay();
        }
      }, 1500);

      injected.current.timeoutId = timeoutId;
      injected.current.nonce = nonce;
    }
  }, [clearInjected, onElementSelected]);

  const disableSelector = useCallback(() => {
    clearInjected();
    setSelectMode(false);
  }, [clearInjected]);

  // also allow toggling with the floating button
  const onToggleSelect = () => {
    if (selectMode) disableSelector(); else enableSelector();
  };

  // close on Escape in parent (for cross-origin overlay)
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && selectMode) {
        disableSelector();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode, disableSelector]);

  return (
    <div ref={containerRef} className={`w-full h-full relative ${className}`}>
      {/* Debug Info Banner - shown when userId or projectId is missing */}
      {(!userId || !projectId) && (
        <div style={{ position: 'absolute', top: 12, left: 12, right: 72, zIndex: 10000 }} className="bg-yellow-500/90 text-black px-4 py-3 rounded-lg text-sm font-medium">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <strong>Preview Configuration Issue:</strong>
              <div className="mt-1">
                {!userId && <div>• User ID is missing</div>}
                {!projectId && <div>• Project ID is missing</div>}
              </div>
              <div className="mt-2 text-xs">
                Current values: userId={String(userId)}, projectId={String(projectId)}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Floating selector button */}
      <button
        onClick={onToggleSelect}
        title={selectMode ? 'Exit element select mode' : 'Select element in preview'}
        style={{ position: 'absolute', right: 12, top: 12, zIndex: 9999 }}
        className={`bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg focus:outline-none ${selectMode ? 'ring-4 ring-blue-300' : ''}`}
      >
        {selectMode ? 'Cancel' : 'Select'}
      </button>

      {/* Small info pill when selection is active */}
      {selectMode && (
        <div style={{ position: 'absolute', left: 12, top: 12, zIndex: 9999 }} className="bg-black/70 text-white text-xs px-3 py-1 rounded">
          Click an element in the preview to capture it; press ESC to cancel
        </div>
      )}
      
      {/* URL Display - shown when not in select mode and userId/projectId exist */}
      {!selectMode && userId && projectId && (
        <div style={{ position: 'absolute', left: 12, top: 12, zIndex: 9999 }} className="bg-black/70 text-white text-xs px-3 py-1 rounded max-w-md truncate">
          Preview URL: {previewSrc}
        </div>
      )}
      {/* Details panel */}
      {details && (
        <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 10000, maxWidth: '60%' }} className="bg-neutral-800/90 text-white p-3 rounded text-sm">
          <div className="flex items-center justify-between mb-2">
            <strong>Element details</strong>
            <button onClick={() => setDetails(null)} className="text-xs px-2 py-1 bg-neutral-700 rounded">Close</button>
          </div>
          {details.error ? (
            <div className="text-xs text-rose-400">{details.error}</div>
          ) : (
            <div className="space-y-1 text-xs">
              <div><strong>Tag:</strong> {details.tagName}</div>
              {details.id && <div><strong>Id:</strong> {details.id}</div>}
              {details.className && <div><strong>Class:</strong> {details.className}</div>}
              {details.boundingClientRect && <div><strong>Bounds:</strong> {Math.round(details.boundingClientRect.x)},{Math.round(details.boundingClientRect.y)} {Math.round(details.boundingClientRect.width)}x{Math.round(details.boundingClientRect.height)}</div>}
              {details.attributes && (
                <div><strong>Attributes:</strong> <span className="truncate block max-w-sm">{details.attributes.map((a: any) => `${a.name}="${a.value}"`).join(' ')}</span></div>
              )}
              {details.computedStyles && (
                <div><strong>Styles:</strong> <span className="block text-xs">{Object.entries(details.computedStyles).map(([k,v]) => `${k}: ${v}`).join('; ')}</span></div>
              )}
              {details.text && <div><strong>Text:</strong> <div className="truncate max-w-sm">{details.text}</div></div>}
            </div>
          )}
        </div>
      )}

      {/* Screenshot preview */}
      {screenshotDataUrl && (
        <div style={{ position: 'absolute', right: 12, bottom: 84, zIndex: 10000 }} className="bg-neutral-800/90 p-2 rounded">
          <div className="flex items-center justify-between gap-2">
            <strong className="text-sm text-white">Screenshot</strong>
            <div className="flex gap-2">
              <a href={screenshotDataUrl} download="preview.png" className="text-xs bg-neutral-700 px-2 py-1 rounded">Download</a>
              <button onClick={() => setScreenshotDataUrl(null)} className="text-xs bg-neutral-700 px-2 py-1 rounded">Close</button>
            </div>
          </div>
          <img src={screenshotDataUrl} alt="screenshot" style={{ maxWidth: 320, maxHeight: 240, display: 'block', marginTop: 8 }} />
        </div>
      )}

      <iframe
        ref={iframeRef}
        title={title}
        src={previewSrc}
        className="w-full h-full border-0"
        style={{ width: '100%', height: '100%', border: 0 }}
        frameBorder="0"
        allow={allow}
        allowFullScreen={allowFullScreen}
      />

      {/* Show selected info with Copy and Close actions */}
      {selectedInfo && (
        <div style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 9999, maxWidth: '60%' }} className="bg-neutral-900/80 text-white p-2 rounded text-sm">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div><strong>Selection:</strong> {selectedInfo.sameOrigin ? selectedInfo.tagName : 'cross-origin'}</div>
              {selectedInfo.cssPath && <div className="truncate" title={selectedInfo.cssPath}>{selectedInfo.cssPath}</div>}
              {!selectedInfo.sameOrigin && <div className="text-xs opacity-80">Coordinates: {Math.round(selectedInfo.x)},{Math.round(selectedInfo.y)}</div>}
            </div>
            <div className="flex items-center gap-2 ml-2">
              <button
                onClick={async () => {
                  // request detailed info
                  if (!selectedInfo) return;
                  setDetails(null);
                  setScreenshotDataUrl(null);
                  // same-origin: query iframe DOM
                  try {
                    const iframe = iframeRef.current;
                    if (selectedInfo.sameOrigin && iframe && iframe.contentDocument) {
                      const doc = iframe.contentDocument as Document;
                      const el = selectedInfo.cssPath ? doc.querySelector(selectedInfo.cssPath) : null;
                      if (el) {
                        const r = el.getBoundingClientRect();
                        const attrs = Array.from(((el as Element).attributes as NamedNodeMap) || []).map((a: Attr) => ({ name: a.name, value: a.value }));
                        const computed = (iframe.contentWindow as Window).getComputedStyle(el as Element);
                        const computedSubset: Record<string,string> = {};
                        ['display','position','width','height','color','background-color','font-size','margin','padding'].forEach(k => { computedSubset[k] = computed.getPropertyValue(k); });
                        const det = {
                          tagName: (el as Element).tagName.toLowerCase(),
                          id: (el as HTMLElement).id || undefined,
                          className: (el as HTMLElement).className || undefined,
                          attributes: attrs,
                          text: (el as HTMLElement).textContent?.slice(0,500) || '',
                          outerHTML: (el as HTMLElement).outerHTML?.slice(0,2000) || '',
                          boundingClientRect: { x: r.x, y: r.y, width: r.width, height: r.height },
                          computedStyles: computedSubset,
                          cssPath: selectedInfo.cssPath
                        };
                        setDetails(det);
                        return;
                      }
                    }
                  } catch (e) {
                    // fallback to postMessage
                  }

                  // cross-origin: ask child via postMessage (requires handshake)
                  try {
                    const iframe = iframeRef.current;
                    if (!iframe) return;
                    const iframeOrigin = (() => { try { return new URL(previewSrc).origin; } catch { return '*'; } })();
                    const nonce = injected.current?.nonce;
                    if (!nonce) {
                      setDetails({ error: 'No handshake/session available. Enter select mode first.' });
                      return;
                    }
                    const listener = (ev: MessageEvent) => {
                      if (iframeOrigin !== '*' && ev.origin !== iframeOrigin) return;
                      const data = ev.data || {};
                      if (!data || typeof data !== 'object') return;
                      if (data.type === 'preview:details-response' && data.nonce === nonce) {
                        setDetails(data.payload || null);
                        window.removeEventListener('message', listener);
                      }
                    };
                    window.addEventListener('message', listener);
                    iframe.contentWindow?.postMessage({ type: 'preview:get-details', version: '1.0', nonce, payload: { selector: selectedInfo.cssPath, x: selectedInfo.x, y: selectedInfo.y } }, iframeOrigin);
                    // add timeout
                    setTimeout(() => { window.removeEventListener('message', listener); }, 5000);
                  } catch (e) {
                    setDetails({ error: String(e) });
                  }
                }}
                className="bg-neutral-700 hover:bg-neutral-600 text-white text-xs px-2 py-1 rounded"
                title="Get detailed element info"
              >
                Details
              </button>

              <button
                onClick={async () => {
                  // request screenshot
                  if (!selectedInfo) return;
                  setScreenshotDataUrl(null);
                  // same-origin: run html2canvas inside iframe
                  try {
                    const iframe = iframeRef.current;
                    if (selectedInfo.sameOrigin && iframe && iframe.contentWindow && iframe.contentDocument) {
                      const win = iframe.contentWindow as any;
                      const doc = iframe.contentDocument as Document;
                      const target = selectedInfo.cssPath ? doc.querySelector(selectedInfo.cssPath) : doc.body;
                      // load html2canvas in iframe if needed
                      if (typeof win.html2canvas === 'undefined') {
                        await new Promise((resolve, reject) => {
                          const s = doc.createElement('script');
                          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
                          s.onload = resolve; s.onerror = reject; doc.head.appendChild(s);
                        });
                      }
                      const canvas = await win.html2canvas(target, { useCORS: true, logging: false });
                      const dataUrl = canvas.toDataURL('image/png');
                      setScreenshotDataUrl(dataUrl);
                      return;
                    }
                  } catch (e) {
                    // fallthrough to postMessage
                  }

                  // cross-origin: ask child to take screenshot
                  try {
                    const iframe = iframeRef.current;
                    if (!iframe) return;
                    const iframeOrigin = (() => { try { return new URL(previewSrc).origin; } catch { return '*'; } })();
                    const nonce = injected.current?.nonce;
                    if (!nonce) {
                      setDetails({ error: 'No handshake/session available. Enter select mode first.' });
                      return;
                    }
                    const listener = (ev: MessageEvent) => {
                      if (iframeOrigin !== '*' && ev.origin !== iframeOrigin) return;
                      const data = ev.data || {};
                      if (!data || typeof data !== 'object') return;
                      if (data.type === 'preview:screenshot-response' && data.nonce === nonce) {
                        if (data.payload && data.payload.dataUrl) setScreenshotDataUrl(data.payload.dataUrl);
                        else setDetails({ error: data.payload && data.payload.error ? data.payload.error : 'unknown error' });
                        window.removeEventListener('message', listener);
                      }
                    };
                    window.addEventListener('message', listener);
                    iframe.contentWindow?.postMessage({ type: 'preview:take-screenshot', version: '1.0', nonce, payload: { selector: selectedInfo.cssPath, x: selectedInfo.x, y: selectedInfo.y } }, iframeOrigin);
                    setTimeout(() => { window.removeEventListener('message', listener); }, 10000);
                  } catch (e) {
                    setDetails({ error: String(e) });
                  }
                }}
                className="bg-neutral-700 hover:bg-neutral-600 text-white text-xs px-2 py-1 rounded"
                title="Capture screenshot of element or page"
              >
                Screenshot
              </button>
              <button
                onClick={async () => {
                  try {
                    const textToCopy = selectedInfo.sameOrigin && selectedInfo.cssPath
                      ? selectedInfo.cssPath
                      : (!selectedInfo.sameOrigin && typeof selectedInfo.x === 'number')
                        ? `${Math.round(selectedInfo.x)},${Math.round(selectedInfo.y)}`
                        : JSON.stringify(selectedInfo);
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(textToCopy);
                    } else {
                      // fallback
                      const ta = document.createElement('textarea');
                      ta.value = textToCopy;
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      ta.parentNode?.removeChild(ta);
                    }
                    setCopyStatus('copied');
                    setTimeout(() => setCopyStatus('idle'), 1500);
                  } catch (e) {
                    setCopyStatus('error');
                    setTimeout(() => setCopyStatus('idle'), 1500);
                  }
                }}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs px-2 py-1 rounded"
                title="Copy selector or coordinates"
              >
                Copy
              </button>
              <button
                onClick={() => {
                  setSelectedInfo(null);
                  setCopyStatus('idle');
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1 rounded"
                title="Close selection"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toast for copy confirmation */}
      {copyStatus !== 'idle' && (
        <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 10000 }}>
          <div className={`px-4 py-2 rounded shadow-lg text-sm ${copyStatus === 'copied' ? 'bg-emerald-600' : 'bg-rose-600'} text-white`}>
            {copyStatus === 'copied' ? 'Copied to clipboard' : 'Copy failed'}
          </div>
        </div>
      )}
    </div>
  );
};

export default Preview;
